import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TenantSelector } from '../../ResourceDetailModal/components/TenantSelector';
import { CostPoolSelector } from '../../ResourceDetailModal/components/CostPoolSelector';
import Select from 'components/product/components/primitives/Select';
import { getUsageBracketFromPrice, getUsageBracketsFromPrices } from '../../ResourceDetailModal/components/marketplace/tabs/usageBrackets';
import { AZURE_REGIONS } from 'utils/azureRegions';
import { formatPrice } from '../../ResourceDetailModal/utils/formatters';
import { getApiClient } from 'utils/apiClient';
import { useTranslation } from 'utils/i18n';
import marketplaceTranslations from '../translations';

/**
 * Basics Tab - Required configuration fields
 */
export const BasicsTab = ({
  formData: formDataProp,
  handleInputChange,
  tenants,
  loadingTenants,
  dropdownOpen,
  setDropdownOpen,
  setFormData,
  setIsSelectedTenantPartner,
  setIsDevTenant,
  loadCostPools,
  costPools,
  loadingCostPools,
  isDevTenant,
  isSelectedTenantPartner,
  isDevResource,
  isCurrentProductFree,
  isCostPoolRequired,
  setIsDevResource,
  mode,
  selectedProduct,
  selectedPrice,
  prices = [],
  loadingPrices = false,
  handleSubscriptionSelect = null,
  loadPrices = null,
  onClose,
  disabledTenantIds = []
}) => {
  // Ensure formData is always an object so inputs never get value={undefined} (avoids uncontrolled→controlled warning)
  const formData = formDataProp && typeof formDataProp === 'object' ? formDataProp : { name: '', description: '', type: 'bot', tenant_id: '', cost_pool_id: '', configuration: {} };
  const { language } = useTranslation();
  const isResaleFromForm = formData.configuration?.is_resale_product === true;
  const getPriceLookupKey = (price, isResale = isResaleFromForm) => {
    if (isResale && price?.id && String(price.id).startsWith('price_')) return price.id;
    return price?.lookup_key || price?.id || '';
  };
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  
  // eslint-disable-next-line no-unused-vars
  const [autoCreateCostPool, setAutoCreateCostPool] = useState(false);
  const [creatingCostPool, setCreatingCostPool] = useState(false);
  const [pendingCostPoolId, setPendingCostPoolId] = useState(null);
  const [selectedUsageBracketKey, setSelectedUsageBracketKey] = useState(null);
  
  // Initialize region if not set
  useEffect(() => {
    if (!formData.configuration?.region) {
      setFormData(prev => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          region: 'germanywestcentral'
        }
      }));
    }
  }, [formData.configuration?.region, setFormData]);
  
  // Auto-select cost pool after it's been created and loaded (works for both dev and non-dev resources)
  useEffect(() => {
    if (pendingCostPoolId && costPools.length > 0 && !loadingCostPools) {
      // Check if the pending cost pool is now in the list
      const costPoolExists = costPools.some(cp => cp.id === pendingCostPoolId);
      if (costPoolExists && formData.cost_pool_id !== pendingCostPoolId) {
        console.log('🎯 Auto-selecting newly created cost pool:', pendingCostPoolId, 'from list:', costPools.map(cp => ({ id: cp.id, name: cp.name })));
        setFormData(prev => {
          const updated = {
            ...prev,
            cost_pool_id: pendingCostPoolId
          };
          console.log('✅ Updated formData with cost_pool_id:', updated.cost_pool_id);
          return updated;
        });
        setPendingCostPoolId(null); // Clear pending
      } else if (!costPoolExists) {
        console.log('⏳ Waiting for cost pool to appear in list:', pendingCostPoolId, 'current list:', costPools.map(cp => cp.id));
      } else if (formData.cost_pool_id === pendingCostPoolId) {
        console.log('✅ Cost pool already selected:', pendingCostPoolId);
        setPendingCostPoolId(null); // Clear pending since it's already selected
      }
    }
  }, [pendingCostPoolId, costPools, loadingCostPools, formData.cost_pool_id, setFormData]);
  
  // Load prices when product and tenant are selected (configuration step)
  // Use a ref to track if we've already loaded prices for this product+tenant combination
  const pricesLoadedRef = useRef(null);
  
  useEffect(() => {
    const priceKey = `${selectedProduct?.id}-${formData.tenant_id}`;
    
    if (loadPrices && selectedProduct?.id && formData.tenant_id && !loadingPrices) {
      // Only load if we haven't loaded for this product+tenant combination yet
      if (pricesLoadedRef.current !== priceKey) {
        console.log('💰 Loading prices for configuration step:', {
          productId: selectedProduct.id,
          tenantId: formData.tenant_id,
          priceKey
        });
        pricesLoadedRef.current = priceKey;
        loadPrices(selectedProduct.id, formData.tenant_id);
      }
    }
    
    // Reset the ref if product or tenant changes
    if (!selectedProduct?.id || !formData.tenant_id) {
      pricesLoadedRef.current = null;
    }
  }, [loadPrices, selectedProduct?.id, formData.tenant_id, loadingPrices]);

  // Always On toggle logic
  const metadata = selectedProduct?.metadata || {};
  const isHosted = metadata.hosted === 'true';
  const isBesperNative = metadata.besper_native === 'true';
  const isBot = metadata.resource_type === 'bot';
  const canEnableAlwaysOn = isHosted;
  const alwaysOnIncluded = isBesperNative || isBot;
  const alwaysOnEnabled = formData.configuration?.always_on === true;
  const alwaysOnCost = 70; // EUR per month

  const handleRegionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        region: value
      }
    }));
  };

  const handleAlwaysOnChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        always_on: checked
      }
    }));
  };

  // Get region options with (Upcoming) suffix for disabled regions
  const regionOptions = AZURE_REGIONS.map(region => ({
    value: region.value,
    label: region.enabled ? region.label : `${region.label} (Upcoming)`,
    disabled: !region.enabled
  }));

  // Backend returns only prices the user should see (tenant + product type). Frontend only filters by dev-resource toggle.
  const filteredPrices = useMemo(() => {
    if (!prices?.length) return [];
    const allowDev = selectedProduct?.metadata?.allow_dev_resource !== false &&
                     selectedProduct?.metadata?.allow_dev_resource !== 'false';
    if (isDevResource && allowDev) {
      return prices.filter(p => p.metadata?.tier === 'development');
    }
    return prices.filter(p => p.metadata?.tier !== 'development');
  }, [prices, isDevResource, selectedProduct?.metadata?.allow_dev_resource]);

  const usageBrackets = useMemo(
    () => getUsageBracketsFromPrices(filteredPrices),
    [filteredPrices]
  );
  const hasUsageVariants = usageBrackets.length > 1;
  const effectiveBracketKey = selectedUsageBracketKey ?? usageBrackets[0]?.key ?? null;
  const pricesToShow = useMemo(() => {
    if (!hasUsageVariants || !effectiveBracketKey) return filteredPrices;
    return filteredPrices.filter(
      (p) => getUsageBracketFromPrice(p)?.key === effectiveBracketKey
    );
  }, [filteredPrices, hasUsageVariants, effectiveBracketKey]);

  if (process.env.NODE_ENV !== 'production' && hasUsageVariants && pricesToShow.length === 0 && filteredPrices.length > 0) {
    console.warn('[BasicsTab] Usage variants on but no prices for bracket:', effectiveBracketKey, 'brackets:', usageBrackets.map((b) => b.key));
  }

  useEffect(() => {
    if (!hasUsageVariants || !formData.configuration?.price_lookup_key) return;
    const key = formData.configuration.price_lookup_key;
    const price = filteredPrices.find(
      (p) => p.lookup_key === key || p.id === key
    );
    const bracket = price ? getUsageBracketFromPrice(price) : null;
    if (bracket && bracket.key !== selectedUsageBracketKey) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[BasicsTab] Syncing capacity dropdown from price_lookup_key:', key, '-> bracket:', bracket.key);
      }
      setSelectedUsageBracketKey(bracket.key);
    }
  }, [hasUsageVariants, formData.configuration?.price_lookup_key, filteredPrices, selectedUsageBracketKey]);

  // Auto-select development tier price when dev resource is enabled
  // Set price and clear cost pool in one update to avoid effect loop with cost pool auto-select
  useEffect(() => {
    if (isDevResource && selectedProduct?.id && filteredPrices.length > 0) {
      // Find the first development tier price (should be only one)
      const developmentPrice = filteredPrices[0];
      
      if (developmentPrice) {
        const priceKey = getPriceLookupKey(developmentPrice, false);
        const currentPriceKey = formData.configuration?.price_lookup_key;
        
        // Only update if not already selected
        if (currentPriceKey !== priceKey) {
          console.log('🎯 Auto-selecting development tier price:', priceKey);
          setFormData(prev => ({
            ...prev,
            configuration: {
              ...prev.configuration,
              price_lookup_key: priceKey
            }
          }));
        }
      }
    } else if (!isDevResource && formData.configuration?.price_lookup_key) {
      // Clear development tier price when dev resource is disabled
      const currentPrice = filteredPrices.find(p => 
        p.id === formData.configuration.price_lookup_key || 
        p.lookup_key === formData.configuration.price_lookup_key
      );
      
      // Standardized metadata: check price.metadata.tier === 'development'
      if (currentPrice && currentPrice.metadata?.tier === 'development') {
        console.log('🧹 Clearing development tier price');
        setFormData(prev => ({
          ...prev,
          configuration: {
            ...prev.configuration,
            price_lookup_key: ''
          }
        }));
      }
    }
  }, [isDevResource, selectedProduct?.id, filteredPrices, formData.configuration?.price_lookup_key, setFormData]);

  // Check if selected price is free (unit_amount === 0)
  // Standardized: free prices are determined by unit_amount, not by tier metadata
  // moved before useEffect that uses it
  const currentPrice = selectedPrice || (formData.configuration?.price_lookup_key && filteredPrices.find(p => 
    p.id === formData.configuration.price_lookup_key || 
    p.lookup_key === formData.configuration.price_lookup_key
  ));
  const isPriceZero = currentPrice && currentPrice.unit_amount === 0;
  
  // Also check if any of the filtered prices are free (for initial loading when no price is selected yet)
  const hasFreePrices = filteredPrices.some(p => p.unit_amount === 0);
  
  // Auto-select first available cost pool when cost pools are loaded and no cost pool is selected
  // Skip when current price is free (partner-free or dev) to avoid loop with "free product → deselect cost pool"
  const priceKeyForFreeCheck = formData.configuration?.price_lookup_key ?? '';
  const isFreeOrPartnerFreePrice = isPriceZero || (typeof priceKeyForFreeCheck === 'string' && priceKeyForFreeCheck.endsWith('-partner-free'));
  useEffect(() => {
    // Only auto-select if:
    // - Cost pools are loaded
    // - No cost pool is currently selected
    // - Not loading
    // - Not a dev tenant (dev tenants don't need cost pools)
    // - Not waiting for a newly created cost pool
    // - Current price is not free/partner-free (otherwise we'd fight with "free product → deselect cost pool")
    if (
      costPools.length > 0 && 
      !formData.cost_pool_id && 
      !loadingCostPools && 
      !isDevTenant &&
      !pendingCostPoolId &&
      !isFreeOrPartnerFreePrice
    ) {
      // All cost pools in the list already have valid_payment_method === true
      // (filtered in loadCostPools), so just select the first one
      const firstCostPool = costPools[0];
      
      if (firstCostPool) {
        console.log('🎯 Auto-selecting first cost pool with valid payment method:', firstCostPool.id, {
          name: firstCostPool.name,
          tenant_id: firstCostPool.tenant_id,
          valid_payment_method: firstCostPool.valid_payment_method
        });
        setFormData(prev => ({
          ...prev,
          cost_pool_id: firstCostPool.id
        }));
      }
    }
  }, [costPools, formData.cost_pool_id, formData.tenant_id, formData.configuration?.price_lookup_key, loadingCostPools, isDevTenant, isDevResource, isPriceZero, hasFreePrices, isFreeOrPartnerFreePrice, pendingCostPoolId, setFormData]);
  
  // Reload cost pools when price changes from free to paid or vice versa
  // Free resources can use inactive cost pools, paid resources need active ones
  // Also reload when filtered prices change (to detect if product has free prices)
  const prevIsPriceZeroRef = useRef(isPriceZero);
  const prevIsDevResourceRef = useRef(isDevResource);
  const prevHasFreePricesRef = useRef(hasFreePrices);
  useEffect(() => {
    const wasPriceFree = prevIsPriceZeroRef.current;
    const isNowPriceFree = isPriceZero;
    const wasDevResource = prevIsDevResourceRef.current;
    const isNowDevResource = isDevResource;
    const hadFreePrices = prevHasFreePricesRef.current;
    const nowHasFreePrices = hasFreePrices;
    
    // Reload if price changed from free to paid or vice versa, or if dev resource state changed
    // OR if the product's available prices changed (e.g., prices loaded and product has free prices)
    const priceChanged = wasPriceFree !== isNowPriceFree;
    const devResourceChanged = wasDevResource !== isNowDevResource;
    const freePricesChanged = hadFreePrices !== nowHasFreePrices;
    
    // Load cost pools when price or dev resource state changes
    // Now we load ALL cost pools (not filtered by tenant), but still need tenantId to check if it's a dev tenant
    if ((priceChanged || devResourceChanged || freePricesChanged) && loadCostPools && !loadingCostPools) {
      // If tenant is selected, use it to check if it's a dev tenant
      // Otherwise, pass null to load all cost pools
      const tenantIdForCheck = formData.tenant_id || null;
      console.log('🔄 Price or dev resource state changed, reloading all cost pools:', { 
        wasPriceFree,
        isNowPriceFree,
        wasDevResource,
        isNowDevResource,
        hadFreePrices,
        nowHasFreePrices,
        tenantIdForCheck
      });
      loadCostPools(tenantIdForCheck, false); // Always filter to valid payment methods only
    }
    
    // Update refs for next render
    prevIsPriceZeroRef.current = isPriceZero;
    prevIsDevResourceRef.current = isDevResource;
    prevHasFreePricesRef.current = hasFreePrices;
  }, [isPriceZero, isDevResource, hasFreePrices, formData.tenant_id, loadCostPools, loadingCostPools]);

  // Auto-select default price from Stripe product OR first available price
  // This runs for non-dev resources (dev resources are handled by the effect above)
  // Never overwrite when price_lookup_key is already a Stripe price ID (e.g. from SDK URL) – backend needs it for resale
  useEffect(() => {
    const currentKey = formData.configuration?.price_lookup_key;
    const isStripePriceId = currentKey && String(currentKey).startsWith('price_');
    if (isStripePriceId) return;
    const productIdForPricing = selectedProduct?.id || formData.configuration?.product_id;
    // Only run if dev resource is disabled (dev resource has its own auto-selection logic)
    if (!isDevResource && productIdForPricing && pricesToShow.length > 0 && !currentKey) {
      if (selectedProduct) {
        const defaultPriceId = typeof selectedProduct.default_price === 'string'
          ? selectedProduct.default_price
          : selectedProduct.default_price?.id;

        if (defaultPriceId) {
          const defaultPrice = pricesToShow.find(price =>
            price.id === defaultPriceId || price.lookup_key === defaultPriceId
          );

          if (defaultPrice) {
            const priceKey = getPriceLookupKey(defaultPrice);
            console.log('🎯 Auto-selecting default price from Stripe:', priceKey);
            setFormData(prev => ({
              ...prev,
              configuration: {
                ...prev.configuration,
                price_lookup_key: priceKey
              }
            }));

            if (handleSubscriptionSelect) {
              handleSubscriptionSelect(defaultPrice);
            }
            return;
          }
        }
      }
      
      // Fallback: If no default price or default price not in filtered list,
      // check if there are any non-free prices (unit_amount > 0)
      // Standardized: free prices are determined by unit_amount === 0
      const nonFreePrices = pricesToShow.filter(price => price.unit_amount > 0);
      
      // If no non-free prices, auto-select the free price (unit_amount === 0)
      if (nonFreePrices.length === 0) {
        const freePrice = pricesToShow.find(price => price.unit_amount === 0);
        
        if (freePrice) {
          const priceKey = getPriceLookupKey(freePrice);
          console.log('🎯 Auto-selecting free price (no non-free prices available):', priceKey);
          setFormData(prev => ({
            ...prev,
            configuration: {
              ...prev.configuration,
              price_lookup_key: priceKey
            }
          }));
          
          if (handleSubscriptionSelect) {
            handleSubscriptionSelect(freePrice);
          }
        }
      } else {
        // If there are non-free prices but no default price, select the first one
        const firstPrice = nonFreePrices[0];
        const priceKey = getPriceLookupKey(firstPrice);
        console.log('🎯 Auto-selecting first non-free price (no default price set):', priceKey);
        setFormData(prev => ({
          ...prev,
          configuration: {
            ...prev.configuration,
            price_lookup_key: priceKey
          }
        }));
        
        if (handleSubscriptionSelect) {
          handleSubscriptionSelect(firstPrice);
        }
      }
    }
  }, [isDevResource, selectedProduct?.id, selectedProduct?.default_price, formData.configuration?.product_id, pricesToShow, formData.configuration?.price_lookup_key, handleSubscriptionSelect, setFormData]);

  // eslint-disable-next-line no-unused-vars
  const canShowAutoCreate = isPriceZero && formData.tenant_id && !formData.cost_pool_id;
  
  // Determine if we should include all cost pools (for free resources and dev resources)
  // Free resources can use inactive cost pools, paid resources need active ones
  // If product has free prices available, also include all cost pools (user might select free price)
  // eslint-disable-next-line no-unused-vars
  const shouldIncludeAllCostPools = isDevResource || isPriceZero || hasFreePrices;

  // --- Resale PayPal guard ---
  // Connected accounts (resale partners) only support card-based Token cloning.
  // If the selected cost pool's default payment method is not a card, block checkout.
  const isResaleProduct =
    formData.configuration?.is_resale_product === true ||
    selectedProduct?.metadata?.resource_type === 'resale' ||
    !!selectedProduct?.metadata?.from_resale_resource;
  const selectedCostPool = useMemo(() => {
    if (!formData.cost_pool_id || !costPools?.length) return null;
    return costPools.find(cp => cp.id === formData.cost_pool_id) || null;
  }, [formData.cost_pool_id, costPools]);

  const resalePayPalBlocked = useMemo(() => {
    if (!isResaleProduct || !selectedCostPool || isCurrentProductFree()) return false;
    const pms = selectedCostPool.payment_methods;
    if (!Array.isArray(pms) || pms.length === 0) return false;
    const defaultPm = pms.find(pm => pm && pm.is_default);
    if (!defaultPm) return false;
    return defaultPm.type !== 'card';
  }, [isResaleProduct, selectedCostPool, isCurrentProductFree]);

  // Handle price selection
  const handlePriceSelect = (price) => {
    if (hasUsageVariants) {
      const bracket = getUsageBracketFromPrice(price);
      if (bracket) setSelectedUsageBracketKey(bracket.key);
    }
    const isNewPriceFree = price.unit_amount === 0;
    const wasPriceFree = isPriceZero;
    
    // Update formData with selected price. For resale, prefer Stripe price ID when present.
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        price_lookup_key: getPriceLookupKey(price, prev.configuration?.is_resale_product === true)
      }
    }));
    
    // Also call the subscription select handler if provided
    if (handleSubscriptionSelect) {
      handleSubscriptionSelect(price);
    }
    
    // Reload all cost pools if switching between free and paid prices
    // Now we load ALL cost pools (not filtered by tenant)
    if (isNewPriceFree !== wasPriceFree && loadCostPools) {
      const tenantIdForCheck = formData.tenant_id || null;
      console.log('🔄 Price changed from free/paid, reloading all cost pools:', { 
        wasFree: wasPriceFree, 
        isNowFree: isNewPriceFree,
        tenantIdForCheck
      });
      loadCostPools(tenantIdForCheck, false); // Always filter to valid payment methods only
    }
  };

  // Handle auto-create cost pool (for both toggle and button)
  const handleAutoCreateCostPool = async (isToggle = false, checked = false) => {
    console.log('🔧 handleAutoCreateCostPool called', { isToggle, checked, tenant_id: formData.tenant_id, isDevResource });
    
    if (isToggle) {
      setAutoCreateCostPool(checked);
    }

    if ((isToggle && checked) || !isToggle) {
      // Create default cost pool
      if (!formData.tenant_id) {
        console.error('❌ Cannot create cost pool: no tenant selected');
        return;
      }

      console.log('✅ Starting cost pool creation...');
      setCreatingCostPool(true);
      try {
        const defaultCostPoolData = {
          tenant_id: formData.tenant_id,
          name: isDevResource 
            ? `${t('basicsTab.costPool.developmentName', 'Development Cost Pool')} - ${new Date().toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')}`
            : `${t('basicsTab.costPool.defaultName', 'Default Cost Pool')} - ${new Date().toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')}`,
          description: isDevResource
            ? t('basicsTab.costPool.developmentDescription', 'Auto-created cost pool for development resources. Empty cost pool with no payment method required.')
            : t('basicsTab.costPool.defaultDescription', 'Auto-created cost pool for free resources. You can update payment methods and details later.'),
          legal_form: 'individual', // Default value
          vat_id: 'DE000000000', // Placeholder VAT ID (German format: DE + 9 digits) - update with real VAT ID later
          primary_address: {
            line1: 'TBD', // Placeholder - update with real address later
            line2: '',
            city: 'TBD', // Placeholder - update with real city later
            postal_code: '00000', // Placeholder - update with real postal code later
            country: 'DE', // Default to Germany
            state: ''
          },
          valid_payment_method: false // No payment method initially
        };

        console.log('📤 Calling apiClient.post to create cost pool with data:', defaultCostPoolData);
        const apiClient = getApiClient();
        const response = await apiClient.post('/v1/cost-pools', defaultCostPoolData, {
          timeout: 360000 // 6 minutes - PDF generation can take up to 3 minutes
        });
        console.log('📥 API response:', response);
        
        // Handle response format: API returns {success: true, cost_pool_id: "..."}
        const costPoolId = response.cost_pool_id || response.data?.id || response.data?.cost_pool_id;
        
        if (response.success && costPoolId) {
          console.log('✅ Cost pool created successfully, ID:', costPoolId);
          
          // Reload cost pools FIRST to include the new one in the dropdown
          // Pass includeAll=true for dev resources and free prices so cost pools without valid payment methods are included
          if (loadCostPools) {
            const tenantIdForCheck = formData.tenant_id || null;
            console.log('🔄 Reloading all cost pools (not filtered by tenant), tenantIdForCheck:', tenantIdForCheck);
            await loadCostPools(tenantIdForCheck, false); // Always filter to valid payment methods only
            console.log('✅ Cost pools reloaded');
          }
          
          // Set pending cost pool ID - useEffect will auto-select it once it's in the list
          setPendingCostPoolId(costPoolId);
          console.log('📝 Set pendingCostPoolId to:', costPoolId);
          
          // Also immediately set the cost pool ID so it's selected right away
          setFormData(prev => {
            const updated = {
              ...prev,
              cost_pool_id: costPoolId
            };
            console.log('✅ Immediately setting cost_pool_id to:', costPoolId);
            return updated;
          });

          if (isToggle) {
            setAutoCreateCostPool(true);
          }
        } else {
          console.error('Failed to create cost pool:', response.error || response.message || t('basicsTab.errors.unknownError', 'Unknown error'));
          if (isToggle) {
            setAutoCreateCostPool(false);
          }
        }
      } catch (error) {
        console.error(t('basicsTab.errors.createCostPoolError', 'Error creating cost pool:'), error);
        if (isToggle) {
          setAutoCreateCostPool(false);
        }
      } finally {
        setCreatingCostPool(false);
      }
    } else if (isToggle && !checked && formData.cost_pool_id) {
      // Clear cost pool if toggle is turned off (but only if it was auto-created)
      // For now, we'll keep it - user can manually change it
    }
  };

  // Handle auto-create cost pool toggle
  // eslint-disable-next-line no-unused-vars
  const handleAutoCreateCostPoolChange = async (e) => {
    await handleAutoCreateCostPool(true, e.target.checked);
  };

  return (
    <div className="basics-tab">
      <div className="tab-section">
        
        {/* Basic Information Box */}
        <div className="basics-section-box">
          <div className="section-box-header">
            <h4 className="section-box-title">{t('basicsTab.basicInformation', 'Basic Information')}</h4>
          </div>
          {/* Grid Layout with explicit rows for alignment */}
          <div className="basics-grid-layout">
            <div className="basics-grid-row">
              <div className="basics-grid-left">
                <div className="form-group">
                  <label htmlFor="name">{t('basicsTab.resourceName', 'Resource Name')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name ?? ''}
                    onChange={handleInputChange}
                    placeholder={t('basicsTab.resourceNamePlaceholder', 'e.g., Customer Support Bot')}
                    required
                  />
                  <small className="form-hint">
                    {t('basicsTab.resourceNameHint', 'Choose a unique name for this resource. This name will be used to identify your resource.')}
                  </small>
                </div>
              </div>

              <div className="basics-grid-right">
                <TenantSelector
                  formData={formData}
                  tenants={tenants}
                  loadingTenants={loadingTenants}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                  setFormData={setFormData}
                  setIsSelectedTenantPartner={setIsSelectedTenantPartner}
                  setIsDevTenant={setIsDevTenant}
                  loadCostPools={(tenantId) => {
                    // Load all cost pools (not filtered by tenant)
                    // tenantId is only used to check if it's a dev tenant
                    loadCostPools(tenantId || null, false); // Always filter to valid payment methods only
                  }}
                  loadPrices={() => {}}
                  handleRetryTenants={() => {}}
                  mode={mode}
                  disabledTenantIds={disabledTenantIds}
                />
              </div>
            </div>

            <div className="basics-grid-row">
              <div className="basics-grid-left">
                <div className="form-group">
                  <label htmlFor="description">{t('basicsTab.description', 'Description')}</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description ?? ''}
                    onChange={handleInputChange}
                    placeholder={t('basicsTab.descriptionPlaceholder', 'Describe what this resource does...')}
                    rows="3"
                  />
                  <small className="form-hint">
                    {t('basicsTab.descriptionHint', 'Optional description to help identify and manage this resource.')}
                  </small>
                </div>
              </div>
              <div className="basics-grid-right">
                <div className="form-group">
                  <CostPoolSelector
                    formData={formData}
                    costPools={costPools}
                    loadingCostPools={loadingCostPools}
                    loading={false}
                    isDevTenant={isDevTenant}
                    isSelectedTenantPartner={isSelectedTenantPartner}
                    isDevResource={isDevResource}
                    isCurrentProductFree={isCurrentProductFree}
                    isCostPoolRequired={isCostPoolRequired}
                    handleInputChange={handleInputChange}
                    onClose={onClose}
                    autoCreateCostPool={false}
                    onAutoCreateCostPool={async () => {
                      console.log('🔘 onAutoCreateCostPool callback invoked from BasicsTab');
                      return await handleAutoCreateCostPool(false, false);
                    }}
                    creatingCostPool={creatingCostPool}
                    isFreePrice={isPriceZero}
                  />
                  {/* Resale PayPal warning */}
                  {resalePayPalBlocked && (
                    <div className="resale-paypal-warning" style={{
                      marginTop: '12px',
                      padding: '12px 16px',
                      backgroundColor: 'var(--warning-bg, #fff3cd)',
                      border: '1px solid var(--warning-border, #ffc107)',
                      borderRadius: '8px',
                      color: 'var(--warning-text, #856404)'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                        Credit/debit card required
                      </div>
                      <div style={{ fontSize: '13px', lineHeight: '1.45' }}>
                        Marketplace resale purchases require a credit or debit card as the default payment method. PayPal and other non-card methods cannot be used for third-party products.
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const cpId = formData.cost_pool_id || '';
                          const tid = formData.tenant_id || '';
                          window.open(`/payment-profile/${cpId}?tab=payment-methods&tenant_id=${tid}`, '_blank');
                        }}
                        style={{
                          marginTop: '10px',
                          padding: '6px 14px',
                          fontSize: '13px',
                          fontWeight: 500,
                          borderRadius: '6px',
                          border: '1px solid var(--warning-border, #ffc107)',
                          backgroundColor: 'transparent',
                          color: 'inherit',
                          cursor: 'pointer'
                        }}
                      >
                        Go to Payment Profile
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price Selection Box - Show when we have a product (selected or from URL e.g. resale deep link) */}
        {(selectedProduct || formData.configuration?.product_id) && (
          <div className="basics-section-box">
            <h4 className="section-box-title">{t('basicsTab.pricingPlan', 'Pricing Plan')} *</h4>
            <div className="form-group">
              {hasUsageVariants && (
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label htmlFor="usage-bracket">Capacity</label>
                  <Select
                    id="usage-bracket"
                    value={effectiveBracketKey || ''}
                    onChange={(value) => {
                      setSelectedUsageBracketKey(value || null);
                      const inNewBracket = filteredPrices.filter(
                        (p) => getUsageBracketFromPrice(p)?.key === value
                      );
                      const currentKey = formData.configuration?.price_lookup_key;
                      const currentInNew = inNewBracket.some(
                        (p) => p.lookup_key === currentKey || p.id === currentKey
                      );
                      if (!currentInNew && inNewBracket.length > 0) {
                        const nonFree = inNewBracket.filter((p) => p.unit_amount > 0);
                        const first = nonFree[0] || inNewBracket[0];
                        if (first) {
                          const priceKey = getPriceLookupKey(first, formData.configuration?.is_resale_product === true);
                          setFormData(prev => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              price_lookup_key: priceKey
                            }
                          }));
                          if (handleSubscriptionSelect) handleSubscriptionSelect(first);
                        }
                      }
                    }}
                    options={usageBrackets.map((b) => ({ value: b.key, label: b.label }))}
                    placeholder="Select capacity"
                  />
                </div>
              )}
              {loadingPrices ? (
                <div className="loading-text">
                  <div className="loading-spinner"></div>
                  {t('basicsTab.loadingPricingPlans', 'Loading pricing plans...')}
                </div>
              ) : filteredPrices.length === 0 ? (
                <div className="form-text text-muted">
                  {isDevResource 
                    ? t('basicsTab.noPartnerFreePricing', 'No partner-free pricing available for this product. Please disable development resource to see other pricing options.')
                    : t('basicsTab.noPricingPlans', 'No pricing plans available for this product.')
                  }
                </div>
              ) : (
                <div className="price-selection-grid">
                  {pricesToShow.map((price) => {
                    const priceKey = getPriceLookupKey(price);
                    const currentPriceKey = formData.configuration?.price_lookup_key;
                    const isSelected = currentPriceKey === priceKey || 
                                      (selectedPrice && (
                                        selectedPrice.id === priceKey || 
                                        selectedPrice.lookup_key === priceKey
                                      ));
                    const isFree = price.unit_amount === 0;
                    // eslint-disable-next-line no-unused-vars
                    const isPartnerFree = (price.lookup_key || '').toLowerCase().includes('partner-free') || 
                                         (price.lookup_key || '').toLowerCase().includes('partner_free');
                    
                    // Determine interval type
                    const interval = price.recurring?.interval || price.metadata?.interval_type || 'month';
                    const isMonthly = interval === 'month' || interval === 'monthly';
                    const isAnnual = interval === 'year' || interval === 'annual' || interval === 'yearly';
                    
                    // Get product name from selectedProduct, fallback to price product name or metadata (resale may have no selectedProduct)
                    const displayName = selectedProduct?.name || 
                                      price.product?.name || 
                                      price.metadata?.product_name || 
                                      t('basicsTab.product', 'Product');
                    
                    return (
                      <div
                        key={priceKey}
                        className={`price-card ${isSelected ? 'selected' : ''} ${isFree ? 'free-plan' : ''}`}
                        onClick={() => handlePriceSelect(price)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePriceSelect(price);
                          }
                        }}
                      >
                        <div className="price-card-header">
                          <div className="price-card-title-section">
                            <div className="price-card-title">
                              {displayName}
                            </div>
                            <div className="price-badges">
                              {isFree && (
                                <span className="free-badge">Free</span>
                              )}
                              {!isFree && isMonthly && (
                                <span className="interval-badge monthly-badge">Monthly</span>
                              )}
                              {!isFree && isAnnual && (
                                <span className="interval-badge annual-badge">Annual</span>
                              )}
                            </div>
                          </div>
                          <div className="price-radio-wrapper">
<input
                            type="radio"
                            name="price_selection"
                            checked={!!isSelected}
                            onChange={() => handlePriceSelect(price)}
                              className="price-radio"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {isSelected && (
                              <div className="price-selected-checkmark">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6L9 17l-5-5"/>
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="price-card-body">
                          <div className="price-amount-wrapper">
                            <div className="price-amount-free">
                              <span className="price-free-label">
                                {isFree 
                                  ? 'Free' 
                                  : `${formatPrice(price.unit_amount, price.currency)}${price.recurring ? ` / ${price.recurring.interval}` : ''}`
                                }
                              </span>
                            </div>
                          </div>
                          {price.metadata?.description && (
                            <p className="price-description">
                              {price.metadata?.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <small className="form-hint">
                Select a pricing plan for your resource. Free plans require a cost pool (can be auto-created below).
              </small>
            </div>
          </div>
        )}


        {/* Deployment Settings Box */}
        <div className="basics-section-box">
          <h4 className="section-box-title">Deployment Settings</h4>
          {/* Two-Column Layout: Region and Always On */}
          <div className="basics-two-column">
            <div className="form-group">
              <label htmlFor="region">Region *</label>
              <Select
                options={regionOptions}
                value={formData.configuration?.region ?? 'germanywestcentral'}
                onChange={handleRegionChange}
                placeholder="Select a region"
                size="medium"
              />
              <small className="form-hint">
                Select the Azure region where your resource will be deployed.
              </small>
            </div>

            <div className="form-group">
              <div className="always-on-toggle-container">
                <label htmlFor="always_on" className="always-on-label">
                  <input
                    type="checkbox"
                    id="always_on"
                    name="always_on"
                    checked={!!alwaysOnEnabled}
                    onChange={handleAlwaysOnChange}
                    disabled={!canEnableAlwaysOn || alwaysOnIncluded}
                    className="always-on-checkbox"
                  />
                  <span className="always-on-label-text">Always On</span>
                  {alwaysOnEnabled && canEnableAlwaysOn && (
                    <span className="always-on-cost">(+{alwaysOnCost} EUR/month)</span>
                  )}
                </label>
                <small className="form-hint always-on-hint">
                  {alwaysOnIncluded
                    ? ''
                    : !canEnableAlwaysOn
                    ? "Always on does not apply"
                    : `Ensures minimal latency on startup by keeping the resource running continuously. Adds ${alwaysOnCost} EUR/month hosting cost.`
                  }
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


